import { useState, useRef, useCallback, type ReactNode } from 'react';
import { Star, Archive } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useMediaQuery';

interface SwipeableCardProps {
  children: ReactNode;
  onSwipeLeft?: () => void; // Archive
  onSwipeRight?: () => void; // Favorite
  leftAction?: {
    icon: typeof Star;
    label: string;
    color: string;
    bgColor: string;
  };
  rightAction?: {
    icon: typeof Archive;
    label: string;
    color: string;
    bgColor: string;
  };
  disabled?: boolean;
}

const SWIPE_THRESHOLD = 80; // Minimum swipe distance to trigger action
const MAX_SWIPE = 120; // Maximum visual swipe distance

export function SwipeableCard({
  children,
  onSwipeLeft,
  onSwipeRight,
  leftAction = {
    icon: Star,
    label: 'Favorite',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
  },
  rightAction = {
    icon: Archive,
    label: 'Archive',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
  },
  disabled = false,
}: SwipeableCardProps) {
  const isMobile = useIsMobile();
  const [translateX, setTranslateX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || !isMobile) return;
      startXRef.current = e.touches[0].clientX;
      currentXRef.current = e.touches[0].clientX;
      setIsSwiping(true);
    },
    [disabled, isMobile]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isSwiping || disabled || !isMobile) return;

      currentXRef.current = e.touches[0].clientX;
      const diff = currentXRef.current - startXRef.current;

      // Limit the swipe distance with elastic effect
      const limitedDiff = Math.sign(diff) * Math.min(Math.abs(diff), MAX_SWIPE);

      // Only allow swipe in directions where actions are available
      if ((diff > 0 && !onSwipeRight) || (diff < 0 && !onSwipeLeft)) {
        return;
      }

      setTranslateX(limitedDiff);
    },
    [isSwiping, disabled, isMobile, onSwipeLeft, onSwipeRight]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping || disabled || !isMobile) return;

    const diff = translateX;

    // Trigger action if swipe exceeds threshold
    if (diff > SWIPE_THRESHOLD && onSwipeRight) {
      onSwipeRight();
    } else if (diff < -SWIPE_THRESHOLD && onSwipeLeft) {
      onSwipeLeft();
    }

    // Reset position
    setTranslateX(0);
    setIsSwiping(false);
  }, [isSwiping, disabled, isMobile, translateX, onSwipeLeft, onSwipeRight]);

  // On desktop or when disabled, just render children
  if (!isMobile || disabled) {
    return <>{children}</>;
  }

  const LeftIcon = leftAction.icon;
  const RightIcon = rightAction.icon;
  const effectiveTranslateX = translateX;
  const showLeftAction = effectiveTranslateX > 20 && onSwipeRight;
  const showRightAction = effectiveTranslateX < -20 && onSwipeLeft;
  const leftOpacity = Math.min(effectiveTranslateX / SWIPE_THRESHOLD, 1);
  const rightOpacity = Math.min(-effectiveTranslateX / SWIPE_THRESHOLD, 1);

  return (
    <div ref={containerRef} className="relative overflow-hidden">
      {/* Left action background (Favorite) */}
      {onSwipeRight && (
        <div
          className={cn(
            'absolute inset-y-0 left-0 flex items-center justify-start pl-6 transition-opacity',
            leftAction.bgColor
          )}
          style={{
            width: Math.max(effectiveTranslateX, 0),
            opacity: leftOpacity,
          }}
        >
          {showLeftAction && (
            <div className="flex flex-col items-center gap-1">
              <LeftIcon
                className={cn(
                  'h-6 w-6 transition-transform',
                  leftAction.color,
                  translateX > SWIPE_THRESHOLD && 'scale-110'
                )}
              />
              <span className={cn('text-xs font-medium', leftAction.color)}>
                {leftAction.label}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Right action background (Archive) */}
      {onSwipeLeft && (
        <div
          className={cn(
            'absolute inset-y-0 right-0 flex items-center justify-end pr-6 transition-opacity',
            rightAction.bgColor
          )}
          style={{
            width: Math.max(-effectiveTranslateX, 0),
            opacity: rightOpacity,
          }}
        >
          {showRightAction && (
            <div className="flex flex-col items-center gap-1">
              <RightIcon
                className={cn(
                  'h-6 w-6 transition-transform',
                  rightAction.color,
                  -translateX > SWIPE_THRESHOLD && 'scale-110'
                )}
              />
              <span className={cn('text-xs font-medium', rightAction.color)}>
                {rightAction.label}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Card content */}
      <div
        className={cn(
          'relative bg-background',
          !isSwiping && 'transition-transform duration-300 ease-out'
        )}
        style={{
          transform: `translateX(${effectiveTranslateX}px)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}
